// client/src/components/MerchantScanQRC.js

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../CSS/Dashboard.css';
import { firestore, storage, auth } from '../../firebase';
import {
  doc,
  updateDoc,
  getDoc,
  setDoc,
  collection,
  getDocs,
  query,
  where,
  limit,
  serverTimestamp,
  addDoc,
  runTransaction
} from 'firebase/firestore';
import { ref, listAll, getDownloadURL, getMetadata } from 'firebase/storage';
import {
  onAuthStateChanged
} from 'firebase/auth';
import { FaSpinner, FaTimesCircle, FaStar, FaCheck, FaUser } from 'react-icons/fa';
import { sanitize } from '../../utils/sanitize';
import Barcode from 'react-barcode';

const MAX_COMMENT_LENGTH = 280;
const EMPTY_COUNTS = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };

const buildCountsFromDoc = (data) => {
  const next = { ...EMPTY_COUNTS };
  if (!data) return next;
  if (data.counts && typeof data.counts === 'object') {
    for (let i = 1; i <= 5; i += 1) {
      const val = Number(data.counts[i] ?? data.counts[String(i)]) || 0;
      next[i] = val;
    }
    return next;
  }
  if (Array.isArray(data.ratings)) {
    for (let i = 0; i < data.ratings.length; i += 1) {
      const r = Number(data.ratings[i]?.rating);
      if (r >= 1 && r <= 5) next[r] = (next[r] || 0) + 1;
    }
  }
  return next;
};

const MerchantScanQRC = () => {
  // uid = UID du commerçant cible (celui chez qui on valide la fidélité)
  const { uid } = useParams();
  const navigate = useNavigate();

  const [generatedCode, setGeneratedCode] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [validationFailure, setValidationFailure] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  const [userRating, setUserRating] = useState(0);
  const [ratingSubmitted, setRatingSubmitted] = useState(false);
  const [comment, setComment] = useState('');

  const [merchantPhotoURL, setMerchantPhotoURL] = useState(null);
  const [merchantPseudo, setMerchantPseudo] = useState('');
  const [merchantEmail, setMerchantEmail] = useState('');

  // ---------- Données du commerçant (avatar + pseudo)
  const fetchMerchantData = useCallback(async () => {
    try {
      const merchantDocSnap = await getDoc(doc(firestore, 'users', uid));
      if (merchantDocSnap.exists()) {
        const merchantData = merchantDocSnap.data();
        setMerchantPseudo(merchantData?.pseudo || 'Inconnu');
        setMerchantEmail(merchantData?.email || '');
      }

      const profilePhotoRef = ref(storage, `${uid}/photo-profil/`);
      const result = await listAll(profilePhotoRef);
      if (result.items.length > 0) {
        const itemsWithMetadata = await Promise.all(
          result.items.map(async (item) => {
            const metadata = await getMetadata(item);
            const timeCreated = metadata.timeCreated
              ? new Date(metadata.timeCreated)
              : new Date(0);
            return { item, timeCreated };
          })
        );
        itemsWithMetadata.sort((a, b) => b.timeCreated - a.timeCreated);
        const latestItem = itemsWithMetadata[0].item;
        const url = await getDownloadURL(latestItem);
        setMerchantPhotoURL(url);
      } else {
        setMerchantPhotoURL(null);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des données du commerçant :', error);
    }
  }, [uid]);

  // ---------- Génère le code pour le client courant dans fidelity/{merchantUid}/list/{clientUid}
  const generateCodeFromUid = useCallback(async () => {
    setQrLoading(true);
    try {
      const currentUser = auth.currentUser;
      const currentUserId = currentUser?.uid;
      if (!currentUserId) {
        setQrLoading(false);
        return;
      }

      // Récup pseudo client courant
      const userDocSnap = await getDoc(doc(firestore, 'users', currentUserId));
      const userPseudo = userDocSnap.data()?.pseudo || 'Inconnu';

      // Vérifie que la fidélité du commerçant (uid de la route) est active
      const entrepriseQuery = query(
        collection(firestore, 'entreprises'),
        where('userId', '==', uid),
        where('fidelity', '==', 'active'),
        limit(1)
      );
      const entrepriseSnap = await getDocs(entrepriseQuery);
      if (entrepriseSnap.empty) {
        setValidationFailure(true);
        setTimeout(() => setValidationFailure(false), 1500);
        return;
      }

      // Insère/MAJ le code
      const newCode = String(Math.floor(Math.random() * 100000)).padStart(5, '0');
      const listDocRef = doc(firestore, 'fidelity', uid, 'list', currentUserId);
      const listDocSnap = await getDoc(listDocRef);

      if (listDocSnap.exists()) {
        await updateDoc(listDocRef, { code: newCode, createdAt: serverTimestamp() });
      } else {
        await setDoc(listDocRef, {
          uid: currentUserId,
          pseudo: userPseudo,
          code: newCode,
          createdAt: serverTimestamp()
        });
      }
      setGeneratedCode(newCode);
    } catch (error) {
      console.error('Erreur lors de la génération du code :', error);
    } finally {
      setQrLoading(false);
    }
  }, [uid]);

  // ---------- Note
  const handleRating = (rating) => setUserRating(rating);

  const handleClose = async () => {
    // ↳ IMPORTANT : même logique que ClientScanQRC
    //    - on exige un user *avant* de setter ratingSubmitted
    //    - on ne crée pas d’anonyme ici
    //    - en cas d’erreur, on réactive le bouton
    if (userRating === 0 || ratingSubmitted) return;

    const currentUser = auth.currentUser;
    const currentUserId = currentUser?.uid;
    if (!currentUserId) {
      // Au retour "à froid", l'user peut être null un bref instant.
      // On ne bloque pas le bouton et on ne crée pas de nouveau compte.
      return;
    }

    setRatingSubmitted(true);

    try {
      const authorSnap = await getDoc(doc(firestore, 'users', currentUserId));
      const authorPseudo = authorSnap.exists() ? (authorSnap.data()?.pseudo || 'Inconnu') : 'Inconnu';
      const sanitizedComment = sanitize(comment).slice(0, MAX_COMMENT_LENGTH);

      const satisfactionDocRef = doc(firestore, 'Satisfaction', uid);
      await runTransaction(firestore, async (transaction) => {
        const snap = await transaction.get(satisfactionDocRef);
        const existing = snap.exists() ? snap.data() : null;
        const counts = buildCountsFromDoc(existing);
        counts[userRating] = (counts[userRating] || 0) + 1;
        const totalRatings = (Number(existing?.totalRatings) || 0) + 1;
        const totalScore = (Number(existing?.totalScore) || 0) + userRating;

        transaction.set(
          satisfactionDocRef,
          {
            pseudo: merchantPseudo,
            email: merchantEmail,
            counts,
            totalRatings,
            totalScore,
            updatedAt: serverTimestamp()
          },
          { merge: true }
        );
      });

      const feedbacksRef = collection(firestore, 'Satisfaction', uid, 'feedbacks');
      const payload = {
        uid: currentUserId,
        pseudo: authorPseudo,
        rating: userRating,
        source: 'merchant',
        createdAt: serverTimestamp()
      };
      if (sanitizedComment) payload.comment = sanitizedComment;
      await addDoc(feedbacksRef, payload);

      navigate(`/merchantfidviewer/${uid}`);
    } catch (error) {
      console.error('Erreur lors de la soumission de la note :', error);
      setRatingSubmitted(false); // ← redonne la main en cas d’échec réseau/écriture
    }
  };

  // ---------- Flux principal
  useEffect(() => {
    if (!uid) {
      // Dans ton routing, merchantscanqrc est protégé, mais on garde un fallback
      navigate('/merchantcamera');
      return;
    }

    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate('/logingeneral');
        return;
      }
      setAuthChecked(true);

      // Charger données + générer code
      await Promise.all([fetchMerchantData(), generateCodeFromUid()]);
    });

    return () => unsub();
  }, [uid, navigate, fetchMerchantData, generateCodeFromUid]);

  if (!authChecked) {
    return (
      <div className="eb-screen">
        <div className="eb-screen__panel">
          <span className="eb-spinner" aria-hidden="true" />
          <p className="eb-screen__text">Chargement...</p>
        </div>
      </div>
    );
  }

  const overlayIcon = (color) => ({
    position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
    width: 128, height: 128, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 10000, fontSize: '72px', color,
    background: 'rgba(255, 255, 255, 0.92)', boxShadow: 'var(--eb-shadow-lg)'
  });

  return (
    <div
      style={{
        position: 'relative',
        minHeight: '100vh',
        background: 'var(--eb-bg)',
        padding: '24px 16px 48px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start'
      }}
    >
      {/* Animation (spinner) */}
      {qrLoading && (
        <div style={{ ...overlayIcon('#fff'), background: 'rgba(15, 35, 56, 0.55)', fontSize: '56px' }} role="status" aria-label="Chargement">
          <FaSpinner className="spin" />
        </div>
      )}

      {/* Animation (erreur) */}
      {validationFailure && (
        <div style={overlayIcon('var(--eb-danger)')} role="alert" aria-label="Erreur">
          <FaTimesCircle />
        </div>
      )}

      <div
        className="eb-card"
        style={{ width: '100%', maxWidth: 440, padding: '28px 24px', textAlign: 'center' }}
      >
      {/* Commerçant (avatar + nom) */}
      <div style={{ marginBottom: 24, textAlign: 'center' }}>
        {merchantPhotoURL ? (
          <img
            src={merchantPhotoURL}
            alt="Profil du commerçant"
            style={{
              width: 88,
              height: 88,
              borderRadius: '50%',
              objectFit: 'cover',
              display: 'block',
              margin: '0 auto',
              border: '1px solid var(--eb-border)'
            }}
          />
        ) : (
          <span style={{ display: 'inline-flex', width: 88, height: 88, borderRadius: '50%', alignItems: 'center', justifyContent: 'center', background: 'var(--eb-surface-2)', border: '1px solid var(--eb-border)' }}>
            <FaUser size={40} color="var(--eb-subtle)" />
          </span>
        )}
        <h3 style={{ margin: '12px 0 0', fontSize: 20, fontWeight: 700, color: 'var(--eb-text)', overflowWrap: 'anywhere' }}>{merchantPseudo}</h3>
      </div>

      {/* Code + rating */}
      <div style={{ textAlign: 'center' }}>
        {generatedCode && (
          <>
            <div style={{ marginBottom: 24, textAlign: 'center' }}>
              <div
                style={{
                  display: 'inline-flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 8,
                  padding: '14px 16px 16px',
                  borderRadius: 'var(--eb-radius)',
                  border: '1.5px dashed var(--eb-border-strong)',
                  fontSize: 28,
                  fontWeight: 600,
                  fontFamily: 'var(--eb-font-mono)',
                  letterSpacing: '3px',
                  color: 'var(--eb-text)',
                  background: 'var(--eb-surface-2)',
                  maxWidth: 320,
                  width: '100%',
                  boxSizing: 'border-box'
                }}
              >
                <span>{generatedCode}</span>
                <div style={{ width: '100%', paddingTop: 4 }}>
                  <Barcode
                    value={generatedCode}
                    height={32}
                    width={1.6}
                    displayValue={false}
                    background="#ffffff"
                    lineColor="#0f172a"
                    margin={0}
                    svgStyle={{ width: '100%' }}
                  />
                </div>
              </div>
            </div>

            <div style={{ paddingTop: 20, borderTop: '1px solid var(--eb-border)' }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 650, color: 'var(--eb-text)' }}>Notez votre expérience client</h3>
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4, margin: '12px 0' }}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <FaStar
                    key={star}
                    role="button"
                    aria-label={`${star} étoile${star > 1 ? 's' : ''}`}
                    style={{
                      cursor: 'pointer',
                      padding: 4,
                      fontSize: 40,
                      color: userRating >= star ? '#F5B301' : 'var(--eb-border-strong)',
                      transition: 'color .15s ease'
                    }}
                    onClick={() => handleRating(star)}
                  />
                ))}
                {userRating > 0 && (
                  <FaCheck style={{ color: 'var(--eb-success)', fontSize: 20, marginLeft: 8 }} aria-label="Note enregistrée" />
                )}
              </div>
              <div style={{ marginTop: 16, textAlign: 'left' }}>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value.slice(0, MAX_COMMENT_LENGTH))}
                  placeholder="Ajoutez un commentaire (optionnel)"
                  rows={4}
                  className="eb-input"
                  style={{
                    width: '100%',
                    minHeight: 120,
                    padding: '12px 14px',
                    resize: 'vertical',
                    display: 'block',
                    lineHeight: 1.5
                  }}
                />
                <div
                  style={{
                    textAlign: 'right',
                    fontSize: 13,
                    color: 'var(--eb-muted)',
                    marginTop: 6
                  }}
                >
                  {comment.length}/{MAX_COMMENT_LENGTH} caractères
                </div>
              </div>
            </div>

            <button
              onClick={handleClose}
              disabled={userRating === 0 || ratingSubmitted}
              className="eb-btn eb-btn--primary eb-btn--lg eb-btn--block"
              style={{ marginTop: 20 }}
            >
              Fermer
            </button>
          </>
        )}

      </div>
      </div>
    </div>
  );
};

export default MerchantScanQRC;
